<app-breadcrumb
  [title]="t('إصدار التطبيق', 'App Version', 'גרסת אפליקציה')"
  [items]="[t('لوحة التحكم', 'Dashboard', 'לוח בקרה')]"
  [active_item]="t('إعدادات تحديث التطبيق', 'App Update Settings', 'הגדרות עדכון אפליקציה')"
></app-breadcrumb>

<div class="container-fluid av-page">
  <ngx-spinner></ngx-spinner>

  <!-- ── Status Banner ── -->
  <div *ngIf="currentConfig; else noConfig" class="av-banner av-banner--active mb-4">
    <i class="fa fa-check-circle av-banner__icon"></i>
    <div class="av-banner__body">
      <span class="av-banner__label">{{ t('الإعداد النشط', 'Active Config', 'הגדרה פעילה') }}</span>
      <div class="av-banner__meta">
        <span class="av-tag av-tag--latest">
          <i class="fa fa-arrow-up me-1"></i>{{ t('آخر نسخة:', 'Latest:', 'אחרונה:') }}
          <strong class="ms-1">{{ currentConfig.latest_version }}</strong>
        </span>
        <span class="av-tag av-tag--min">
          <i class="fa fa-arrow-down me-1"></i>{{ t('الحد الأدنى:', 'Minimum:', 'מינימום:') }}
          <strong class="ms-1">{{ currentConfig.minimum_required_version }}</strong>
        </span>
        <span class="av-tag av-tag--date">
          <i class="fa fa-clock-o me-1"></i>{{ formatDate(currentConfig.updatedDate) }}
        </span>
      </div>
    </div>
    <button class="btn av-banner__history-btn" (click)="toggleHistory()">
      <i class="fa fa-history me-1"></i>
      {{ showHistory ? t('إخفاء السجل', 'Hide History', 'הסתר') : t('عرض السجل', 'View History', 'הצג היסטוריה') }}
    </button>
  </div>

  <ng-template #noConfig>
    <div class="av-banner av-banner--empty mb-4">
      <i class="fa fa-info-circle av-banner__icon"></i>
      <span class="av-banner__body">
        {{ t('لا توجد إعدادات نشطة — سيتم إنشاء إعداد جديد عند الحفظ.', 'No active config — a new record will be created on save.', 'אין הגדרה פעילה — תיווצר חדשה בעת השמירה.') }}
      </span>
      <button class="btn av-banner__history-btn" (click)="toggleHistory()">
        <i class="fa fa-history me-1"></i>
        {{ showHistory ? t('إخفاء السجل', 'Hide History', 'הסתר') : t('عرض السجل', 'View History', 'הצג היסטוריה') }}
      </button>
    </div>
  </ng-template>

  <div class="row g-4">

    <!-- ══ LEFT COLUMN: Form ══ -->
    <div class="col-xl-8 col-lg-7">

      <form [formGroup]="form" (ngSubmit)="save()">

        <!-- ─ Card 1: Version Numbers ─ -->
        <div class="av-card mb-4">
          <div class="av-card__header">
            <div class="av-card__header-icon av-card__header-icon--primary">
              <i class="fa fa-code-fork"></i>
            </div>
            <div>
              <h6 class="av-card__title">{{ t('إصدارات النسخ', 'Version Numbers', 'מספרי גרסה') }}</h6>
              <p class="av-card__subtitle">{{ t('حدد آخر نسخة والحد الأدنى المطلوب', 'Set the latest release and minimum required version', 'הגדר את הגרסה האחרונה והמינימום הנדרש') }}</p>
            </div>
          </div>
          <div class="av-card__body">
            <div class="row g-4">
              <div class="col-md-6">
                <label class="av-label">
                  {{ t('آخر نسخة متاحة', 'Latest Version', 'גרסה אחרונה') }}
                  <span class="av-required">*</span>
                </label>
                <div class="av-input-wrap" [class.av-input-wrap--error]="invalid('latest_version')">
                  <i class="fa fa-arrow-circle-up av-input-icon"></i>
                  <input
                    type="text"
                    class="av-input"
                    [class.av-input--error]="invalid('latest_version')"
                    formControlName="latest_version"
                    placeholder="2.5.0"
                    autocomplete="off"
                  />
                </div>
                <div class="av-error" *ngIf="form.get('latest_version')?.errors?.['required'] && form.get('latest_version')?.touched">
                  <i class="fa fa-exclamation-circle me-1"></i>{{ t('هذا الحقل مطلوب', 'Required', 'שדה נדרש') }}
                </div>
                <div class="av-error" *ngIf="form.get('latest_version')?.errors?.['semver'] && form.get('latest_version')?.touched">
                  <i class="fa fa-exclamation-circle me-1"></i>{{ t('الصيغة المطلوبة: X.Y.Z', 'Format: X.Y.Z (e.g. 2.5.0)', 'פורמט: X.Y.Z') }}
                </div>
                <p class="av-hint">{{ t('تُحدَّث عند كل رفع على المتجر', 'Update with every store release', 'עדכן עם כל פרסום') }}</p>
              </div>

              <div class="col-md-6">
                <label class="av-label">
                  {{ t('الحد الأدنى المسموح', 'Minimum Required', 'מינימום נדרש') }}
                  <span class="av-required">*</span>
                </label>
                <div class="av-input-wrap" [class.av-input-wrap--error]="invalid('minimum_required_version') || crossFieldError">
                  <i class="fa fa-arrow-circle-down av-input-icon"></i>
                  <input
                    type="text"
                    class="av-input"
                    [class.av-input--error]="invalid('minimum_required_version') || crossFieldError"
                    formControlName="minimum_required_version"
                    placeholder="2.3.0"
                    autocomplete="off"
                  />
                </div>
                <div class="av-error" *ngIf="form.get('minimum_required_version')?.errors?.['required'] && form.get('minimum_required_version')?.touched">
                  <i class="fa fa-exclamation-circle me-1"></i>{{ t('هذا الحقل مطلوب', 'Required', 'שדה נדרש') }}
                </div>
                <div class="av-error" *ngIf="form.get('minimum_required_version')?.errors?.['semver'] && form.get('minimum_required_version')?.touched">
                  <i class="fa fa-exclamation-circle me-1"></i>{{ t('الصيغة المطلوبة: X.Y.Z', 'Format: X.Y.Z (e.g. 2.3.0)', 'פורמט: X.Y.Z') }}
                </div>
                <div class="av-error" *ngIf="crossFieldError">
                  <i class="fa fa-exclamation-circle me-1"></i>{{ t('يجب أن تكون ≤ آخر نسخة', 'Must be ≤ latest version', 'חייבת להיות ≤ גרסה האחרונה') }}
                </div>
                <p class="av-hint">{{ t('ارفعها فقط عند إصلاحات أمان حرجة', 'Raise only for critical security fixes', 'העלה רק לתיקוני אבטחה') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- ─ Card 2: Update Messages ─ -->
        <div class="av-card mb-4">
          <div class="av-card__header">
            <div class="av-card__header-icon av-card__header-icon--info">
              <i class="fa fa-comment"></i>
            </div>
            <div>
              <h6 class="av-card__title">
                {{ t('رسائل التحديث', 'Update Messages', 'הודעות עדכון') }}
                <span class="av-optional-badge">{{ t('اختياري', 'Optional', 'אופציונלי') }}</span>
              </h6>
              <p class="av-card__subtitle">{{ t('إذا تُرك فارغاً، يستخدم السيرفر النصوص الافتراضية', 'If left empty, the server uses its default texts', 'ריק = השרת משתמש בטקסט ברירת מחדל') }}</p>
            </div>
          </div>
          <div class="av-card__body">
            <div class="av-lang-tabs mb-3">
              <button type="button" class="av-lang-tab" [class.av-lang-tab--active]="activeTab === 'ar'" (click)="activeTab = 'ar'">
                <span class="av-lang-flag">🇸🇦</span> العربية
              </button>
              <button type="button" class="av-lang-tab" [class.av-lang-tab--active]="activeTab === 'en'" (click)="activeTab = 'en'">
                <span class="av-lang-flag">🇬🇧</span> English
              </button>
              <button type="button" class="av-lang-tab" [class.av-lang-tab--active]="activeTab === 'he'" (click)="activeTab = 'he'">
                <span class="av-lang-flag">🇮🇱</span> עברית
              </button>
            </div>

            <div *ngIf="activeTab === 'ar'">
              <label class="av-label">{{ t('رسالة التحديث بالعربية', 'Arabic message', 'הודעה בערבית') }}</label>
              <textarea class="av-textarea" rows="3" formControlName="update_message_ar"
                dir="rtl"
                placeholder="يوجد إصدار جديد من التطبيق، ويجب تحديث التطبيق للمتابعة."></textarea>
            </div>
            <div *ngIf="activeTab === 'en'">
              <label class="av-label">{{ t('رسالة التحديث بالإنجليزية', 'English message', 'הודעה באנגלית') }}</label>
              <textarea class="av-textarea" rows="3" formControlName="update_message_en"
                dir="ltr"
                placeholder="A new version is available. Please update to continue."></textarea>
            </div>
            <div *ngIf="activeTab === 'he'">
              <label class="av-label">{{ t('رسالة التحديث بالعبرية', 'Hebrew message', 'הודעה בעברית') }}</label>
              <textarea class="av-textarea" rows="3" formControlName="update_message_he"
                dir="rtl"
                placeholder="קיימת גרסה חדשה. יש לעדכן כדי להמשיך."></textarea>
            </div>
          </div>
        </div>

        <!-- ─ Card 3: Store URLs ─ -->
        <div class="av-card mb-4">
          <div class="av-card__header">
            <div class="av-card__header-icon av-card__header-icon--success">
              <i class="fa fa-link"></i>
            </div>
            <div>
              <h6 class="av-card__title">
                {{ t('روابط المتاجر', 'Store Links', 'קישורי חנות') }}
                <span class="av-optional-badge">{{ t('اختياري', 'Optional', 'אופציונלי') }}</span>
              </h6>
              <p class="av-card__subtitle">{{ t('روابط تحميل التطبيق من متاجر Google و Apple', 'Download links from Google and Apple stores', 'קישורי הורדה מ-Google ו-Apple') }}</p>
            </div>
          </div>
          <div class="av-card__body">
            <div class="row g-4">
              <div class="col-md-6">
                <label class="av-label">
                  <span class="av-store-badge av-store-badge--android">
                    <i class="fa fa-android"></i> Google Play
                  </span>
                </label>
                <div class="av-input-wrap" [class.av-input-wrap--error]="invalid('google_play_url')">
                  <i class="fa fa-android av-input-icon av-input-icon--android"></i>
                  <input
                    type="url"
                    class="av-input"
                    [class.av-input--error]="invalid('google_play_url')"
                    formControlName="google_play_url"
                    placeholder="https://play.google.com/store/apps/details?id=..."
                    dir="ltr"
                  />
                </div>
                <div class="av-error" *ngIf="invalid('google_play_url')">
                  <i class="fa fa-exclamation-circle me-1"></i>{{ t('الرابط طويل جداً (500 حرف)', 'URL too long (max 500 chars)', 'קישור ארוך מדי') }}
                </div>
              </div>
              <div class="col-md-6">
                <label class="av-label">
                  <span class="av-store-badge av-store-badge--apple">
                    <i class="fa fa-apple"></i> App Store
                  </span>
                </label>
                <div class="av-input-wrap" [class.av-input-wrap--error]="invalid('app_store_url')">
                  <i class="fa fa-apple av-input-icon"></i>
                  <input
                    type="url"
                    class="av-input"
                    [class.av-input--error]="invalid('app_store_url')"
                    formControlName="app_store_url"
                    placeholder="https://apps.apple.com/app/id..."
                    dir="ltr"
                  />
                </div>
                <div class="av-error" *ngIf="invalid('app_store_url')">
                  <i class="fa fa-exclamation-circle me-1"></i>{{ t('الرابط طويل جداً (500 حرف)', 'URL too long (max 500 chars)', 'קישור ארוך מדי') }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ─ Active Toggle + Actions ─ -->
        <div class="av-actions-bar">
          <div class="av-toggle-wrap">
            <label class="av-toggle" for="isActiveSwitch">
              <input class="av-toggle__input" type="checkbox" id="isActiveSwitch" formControlName="is_active" />
              <span class="av-toggle__track"></span>
              <span class="av-toggle__label">{{ t('تفعيل الإعداد', 'Activate Config', 'הפעל הגדרה') }}</span>
            </label>
          </div>
          <div class="d-flex gap-3 flex-wrap">
            <button
              type="button"
              class="btn av-btn av-btn--danger"
              *ngIf="currentConfig"
              (click)="confirmDeactivate()"
            >
              <i class="fa fa-ban me-2"></i>
              {{ t('إلغاء التفعيل', 'Deactivate', 'בטל הפעלה') }}
            </button>
            <button type="submit" class="btn av-btn av-btn--primary">
              <i class="fa fa-save me-2"></i>
              {{ t('حفظ الإعدادات', 'Save Settings', 'שמור הגדרות') }}
            </button>
          </div>
        </div>

      </form>
    </div>

    <!-- ══ RIGHT COLUMN: Preview & Info ══ -->
    <div class="col-xl-4 col-lg-5">

      <!-- Impact Preview -->
      <div class="av-card av-card--sticky mb-4">
        <div class="av-card__header">
          <div class="av-card__header-icon av-card__header-icon--warning">
            <i class="fa fa-eye"></i>
          </div>
          <div>
            <h6 class="av-card__title">{{ t('معاينة التأثير', 'Impact Preview', 'תצוגת השפעה') }}</h6>
            <p class="av-card__subtitle">{{ t('كيف تؤثر الإعدادات على المستخدمين', 'How settings affect users', 'כיצד ההגדרות משפיעות') }}</p>
          </div>
        </div>
        <div class="av-card__body p-0">
          <table class="av-preview-table">
            <thead>
              <tr>
                <th>{{ t('نسخة المستخدم', 'User Version', 'גרסת משתמש') }}</th>
                <th>{{ t('النتيجة', 'Result', 'תוצאה') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let ex of [
                  form.get('minimum_required_version')?.value ? decrementVersion(form.get('minimum_required_version')?.value || '') : '—',
                  form.get('minimum_required_version')?.value || '—',
                  midVersion(),
                  form.get('latest_version')?.value || '—'
                ]">
                <td class="av-preview-version">{{ ex }}</td>
                <td>
                  <span *ngIf="ex !== '—'" [ngClass]="impactClass(ex)" class="av-impact-badge">
                    {{ impactLabel(ex) }}
                  </span>
                  <span *ngIf="ex === '—'" class="av-dash">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="av-preview-legend">
          <div class="av-legend-item">
            <span class="av-legend-dot av-legend-dot--danger"></span>
            {{ t('إجباري', 'Forced update', 'עדכון כפוי') }}
          </div>
          <div class="av-legend-item">
            <span class="av-legend-dot av-legend-dot--warning"></span>
            {{ t('اختياري', 'Optional update', 'עדכון אופציונלי') }}
          </div>
          <div class="av-legend-item">
            <span class="av-legend-dot av-legend-dot--success"></span>
            {{ t('لا شيء', 'No update', 'ללא עדכון') }}
          </div>
        </div>
      </div>

      <!-- Logic Info -->
      <div class="av-info-card">
        <div class="av-info-card__title">
          <i class="fa fa-lightbulb-o me-2"></i>
          {{ t('كيف يعمل النظام؟', 'How does it work?', 'כיצד זה עובד?') }}
        </div>
        <ul class="av-info-list">
          <li>
            <span class="av-info-dot av-info-dot--danger"></span>
            <div>
              <strong>{{ t('تحديث إجباري', 'Forced', 'כפוי') }}</strong><br>
              <span>{{ t('نسخة المستخدم أقل من الحد الأدنى', 'User version < minimum required', 'גרסה < מינימום') }}</span>
            </div>
          </li>
          <li>
            <span class="av-info-dot av-info-dot--warning"></span>
            <div>
              <strong>{{ t('تحديث اختياري', 'Optional', 'אופציונלי') }}</strong><br>
              <span>{{ t('نسخة المستخدم بين الحد الأدنى وآخر نسخة', 'Minimum ≤ user < latest', 'מינימום ≤ משתמש < אחרונה') }}</span>
            </div>
          </li>
          <li>
            <span class="av-info-dot av-info-dot--success"></span>
            <div>
              <strong>{{ t('لا تنبيه', 'No alert', 'ללא התראה') }}</strong><br>
              <span>{{ t('نسخة المستخدم مساوية أو أحدث من آخر نسخة', 'User version ≥ latest', 'גרסה ≥ אחרונה') }}</span>
            </div>
          </li>
        </ul>
      </div>

    </div>
  </div>

  <!-- ── History Table ── -->
  <div class="av-card mt-2 mb-4" *ngIf="showHistory">
    <div class="av-card__header">
      <div class="av-card__header-icon av-card__header-icon--secondary">
        <i class="fa fa-history"></i>
      </div>
      <div>
        <h6 class="av-card__title">{{ t('سجل الإعدادات السابقة', 'Settings History', 'היסטוריית הגדרות') }}</h6>
        <p class="av-card__subtitle">{{ t('جميع الإعدادات المحفوظة مرتبة من الأحدث', 'All saved configs, newest first', 'כל ההגדרות השמורות') }}</p>
      </div>
    </div>
    <div class="av-card__body p-0">
      <div *ngIf="loadingHistory" class="av-loading-row">
        <span class="spinner-border spinner-border-sm me-2"></span>
        {{ t('جاري التحميل…', 'Loading…', 'טוען…') }}
      </div>
      <div *ngIf="!loadingHistory && historyRows.length === 0" class="av-empty-row">
        <i class="fa fa-inbox fa-2x mb-2 d-block"></i>
        {{ t('لا توجد سجلات', 'No records found', 'לא נמצאו רשומות') }}
      </div>
      <div class="table-responsive" *ngIf="!loadingHistory && historyRows.length > 0">
        <table class="av-history-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{{ t('آخر نسخة', 'Latest', 'אחרונה') }}</th>
              <th>{{ t('الحد الأدنى', 'Minimum', 'מינימום') }}</th>
              <th>{{ t('الحالة', 'Status', 'סטטוס') }}</th>
              <th>{{ t('تاريخ التحديث', 'Updated', 'עודכן') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of historyRows" [class.av-history-table__row--active]="row.is_active">
              <td class="av-history-id">{{ row.id }}</td>
              <td><span class="av-version-chip av-version-chip--latest">{{ row.latest_version }}</span></td>
              <td><span class="av-version-chip av-version-chip--min">{{ row.minimum_required_version }}</span></td>
              <td>
                <span class="av-status-badge" [class.av-status-badge--active]="row.is_active" [class.av-status-badge--inactive]="!row.is_active">
                  <i class="fa" [class.fa-check-circle]="row.is_active" [class.fa-times-circle]="!row.is_active"></i>
                  {{ row.is_active ? t('نشط', 'Active', 'פעיל') : t('غير نشط', 'Inactive', 'לא פעיל') }}
                </span>
              </td>
              <td class="av-history-date">{{ formatDate(row.updatedDate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

</div>
